import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { Injectable, inject } from '@angular/core';
import { take } from 'rxjs/operators';
import { ListResponse } from 'src/app/core/models/list-response.model';
import { Quotation } from 'src/app/core/models/quotation.model';
import { UserSession } from 'src/app/core/models/user.model';
import { EnvironmentService } from 'src/app/core/services/environment/environment.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { environment } from 'src/environments/environment';
import { AvailableOffer, Order } from '../model/order.model';
import { ShippingCharge } from '../model/shipping-charge.interface';
import { TrackShipment } from '../model/track-shipment.model';

export interface AssignBillingAddress {
  billing_address: string;
  shipping_address: string;
  payment_method?: string;
  quotationId: string;
  opted_shipping_charges: string;
}

export interface AssignShippingAddress {
  shippingAddressId: string;
  quotationId: string;
}
export interface AssignPaymentMethod {
  paymentMethodId: string;
  quotationId: string;
}
export interface AssignPaymentMethodRetry {
  paymentMethodId: string;
  orderId: string;
}
export interface ApplyCouponCode {
  coupon_code: string;
  quotationId: string;
}
export interface RemoveCouponCode {
  coupon_code: string;
  quotationId: string;
}

@Injectable({
  providedIn: 'root'
})
export class OrderApiService {
  private userService = inject(UserService);
  private http = inject(HttpClient);
  private environmentService = inject(EnvironmentService);


  url: string;
  backendBaseUrl: string;
  version1: string;
  version2: string;

  // Get current user from signal-based service
  getCurrentUser(): UserSession {
    return this.userService.appUser;
  }

  // Check if user is authenticated
  isUserAuthenticated(): boolean {
    return this.userService.isAuthenticated() || false;
  }

  constructor() {
    this.url = environment.baseUrl;
    this.backendBaseUrl = environment.backendBaseUrl;
    this.version1 = environment.version1;
    this.version2 = environment.version2;

  }

  assignBillingAddress(reqObj: AssignBillingAddress): Observable<Quotation> {
    const { quotationId, ...rest } = reqObj || {};
    return this.http.patch<Quotation>(`${this.url}/consumer/quotations/${quotationId}/assign_billing_address/`, { ...rest });
  }

  assignShippingAddress(reqObj: AssignShippingAddress): Observable<Quotation> {
    return this.http.patch<Quotation>(`${this.url}/consumer/quotations/${reqObj?.quotationId}/assign_shipping_address/`, {
      shipping_address: reqObj?.shippingAddressId
    });
  }

  assignPaymentMethod(reqObj: AssignPaymentMethod): Observable<Quotation> {
    return this.http.patch<Quotation>(`${this.url}/consumer/quotations/${reqObj?.quotationId}/assign_payment_methods/`, {
      payment_method: reqObj?.paymentMethodId
    });
  }

  assignPaymentMethodRetry(reqObj: AssignPaymentMethodRetry): Observable<Order> {
    return this.http.patch<Order>(`${this.url}/consumer/orders/${reqObj?.orderId}/assign_payment_methods/`, {
      payment_method: reqObj?.paymentMethodId
    });
  }

  validatePaymentMethodRetry(reqObj: AssignPaymentMethodRetry): Observable<Order> {
    return this.http.post<Order>(`${this.url}/consumer/orders/${reqObj?.orderId}/validate_payment_method/`, {
      payment_method: reqObj?.paymentMethodId
    });
  }

  placeOrder(quotationId: string, quotationDetail: any): Observable<Order> {
    return this.http.post<Order>(`${this.url}/consumer/quotations/${quotationId}/generate_order/`,
      quotationDetail
    );
  }

  getPaymentMethods(quotationId: string): Observable<ListResponse<any>> {
    return this.http.get<ListResponse<Order>>(`${this.url}/consumer/quotations/${quotationId}/payment_methods/`);
  }

  getPaymentMethodsForRetry(orderId: string): Observable<ListResponse<any>> {
    return this.http.get<ListResponse<Order>>(`${this.url}/consumer/orders/${orderId}/payment_methods/`);
  }

  getOrders(pageNumber = 0): Observable<ListResponse<Order>> {
    const _environment = this.environmentService.environment();
    const currentUser = this.userService.appUser;

    // Check if user and company are available before making the request
    if (!currentUser?.company?.id) {
      throw new Error('User or company information not available');
    }

    if (!_environment) {
      throw new Error('Environment not available');
    }

    return this.http.get<ListResponse<Order>>(
      `${this.backendBaseUrl}/${this.version2}/consumer/companies/${currentUser.company.id}/orders?ordering=-created_at`, {
      params: new HttpParams()
        .set('seller', _environment.sellerId)
        .set('limit', '20')
        .set('offset', `${20 * pageNumber}`)
    });
  }

  getOrder(orderId: string): Observable<Order> {
    const _environment = this.environmentService.environment();

    if (!_environment) {
      throw new Error('Environment not available');
    }

    return this.http.get<Order>(`${this.backendBaseUrl}/${this.version2}/consumer/orders/${orderId}/`, {
      params: new HttpParams()
        .set('seller', _environment.sellerId)
        .set('expand', 'shipping_method,payment_method')
    });
  }

  getOrderForRetryPayment(orderId: string): Observable<Order> {
    const fields = 'shipping_address,billing_address,id,order_items,subtotal,gst_amount,product_offer_discount,cart_offer_discount,order_total,payment_method,amount_due';
    const _environment = this.environmentService.environment();

    if (!_environment) {
      throw new Error('Environment not available');
    }

    return this.http.get<Order>(`${this.backendBaseUrl}/${this.version1}/consumer/orders/${orderId}/`, {
      params: new HttpParams()
        .set('seller', _environment.sellerId)
        .set('expand', 'shipping_method,payment_method')
        .set('fields', fields)
    });
  }

  trackShipment(shipmentId: string): Observable<TrackShipment> {
    const _environment = this.environmentService.environment();

    if (!_environment) {
      throw new Error('Environment not available');
    }

    return this.http.post<TrackShipment>(`${this.url}/consumer/shipments/${shipmentId}/track_shipment`, {}, {
      params: new HttpParams().set('seller', _environment.sellerId)
    });
  }

  applyCouponCode(reqObj: ApplyCouponCode): Observable<Quotation> {
    return this.http.post(`${this.url}/consumer/quotations/${reqObj?.quotationId}/apply_coupon_code/`, {
      coupon_code: reqObj?.coupon_code
    });
  }

  removeCouponCode(reqObj: RemoveCouponCode): Observable<Quotation> {
    return this.http.post(`${this.url}/consumer/quotations/${reqObj?.quotationId}/remove_coupon_code/`, {
      coupon_code: reqObj?.coupon_code
    });
  }

  updateOrderPaymentStatus(reqObj: any): Observable<any> {
    return this.http.post(`${this.url}/consumer/orders/checkout/success`, reqObj);
  }

  updateOrderPaymentCancelled(orderId: string) {
    return this.http.post(`${this.url}/consumer/orders/${orderId}/checkout_cancelled_by_user`, {});
  }

  applyCashPoints(quotationId: string): Observable<Quotation> {
    return this.http.post(`${this.url}/consumer/quotations/${quotationId}/apply_cash_points/`, {});
  }

  removeCashPoints(quotationId: string): Observable<Quotation> {
    return this.http.post(`${this.url}/consumer/quotations/${quotationId}/remove_cash_points/`, {});
  }

  getAvailableOffers(quotationId: string): Observable<AvailableOffer[]> {
    return this.http.get<AvailableOffer[]>(`${this.url}/consumer/quotations/${quotationId}/available_offers/`);
  }

  refreshPaymentStatus(order_id: string): Observable<{ payment_success: boolean }> {
    const _environment = this.environmentService.environment();

    if (!_environment) {
      throw new Error('Environment not available');
    }

    return this.http.post<{ payment_success: boolean }>(`${this.url}/consumer/orders/${order_id}/recheck_payment_status?seller=${_environment.sellerId}`, {}).pipe(
      take(1)
    );
  }

  getShippingCharges(quotationId: string): Observable<ListResponse<ShippingCharge>> {
    return this.http.get<ListResponse<ShippingCharge>>(`${this.url}/consumer/quotations/${quotationId}/shipping_charges`)
  }

  getOrderPayments(order_id: string): Observable<ListResponse<any>> {
    return this.http.get<ListResponse<any>>(`${this.url}/consumer/orders/${order_id}/order_payments/`, {
      params: new HttpParams()
        .set('expand', 'payment_method')
    })
  }

  recheckOrderPayment(order_payment_id: string): Observable<{ payment_success: boolean }> {
    return this.http.post<{ payment_success: boolean }>(`${this.url}/consumer/order_payments/${order_payment_id}/recheck_payment_status/`, {})
  }

}
